import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

interface ArtworkImageProps {
  src: string;
  alt: string;
  viewMode?: 'grid' | 'list';
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  showLoadingState?: boolean;
}

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.gray.light};
`;

const StyledImage = styled.img<{ $viewMode: 'grid' | 'list'; $loaded: boolean }>`
  width: 100%;
  height: ${({ $viewMode }) => $viewMode === 'grid' ? '150px' : '80px'};
  object-fit: cover;
  background: ${({ theme }) => theme.colors.gray.light};
  opacity: ${({ $loaded }) => $loaded ? 1 : 0};
  transition: opacity ${({ theme }) => theme.transitions.fast};
  cursor: ${({ onClick }) => onClick ? 'pointer' : 'default'};
`;

const LoadingPlaceholder = styled.div<{ $viewMode: 'grid' | 'list'; $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${({ $viewMode }) => $viewMode === 'grid' ? '150px' : '80px'};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.gray.light} 0%,
    ${({ theme }) => theme.colors.white} 50%,
    ${({ theme }) => theme.colors.gray.light} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  transition: opacity ${({ theme }) => theme.transitions.fast};

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const ErrorPlaceholder = styled.div<{ $viewMode: 'grid' | 'list' }>`
  width: 100%;
  height: ${({ $viewMode }) => $viewMode === 'grid' ? '150px' : '80px'};
  background: ${({ theme }) => theme.colors.gray.light};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray.dark};
  font-size: ${({ theme }) => theme.typography.fontSize.xl};
`;

export function ArtworkImage({
  src,
  alt,
  viewMode = 'grid',
  onClick,
  className,
  showLoadingState = true,
}: ArtworkImageProps) {
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');

  const handleLoad = useCallback(() => {
    setLoadState('loaded');
  }, []);

  const handleError = useCallback(() => {
    setLoadState('error');
  }, []);

  if (loadState === 'error') {
    return (
      <ErrorPlaceholder $viewMode={viewMode} className={className}>
        🖼️
      </ErrorPlaceholder>
    );
  }

  return (
    <ImageContainer className={className}>
      {showLoadingState && (
        <LoadingPlaceholder
          $viewMode={viewMode}
          $visible={loadState === 'loading'}
        />
      )}
      <StyledImage
        src={src}
        alt={alt}
        $viewMode={viewMode}
        $loaded={loadState === 'loaded'}
        onClick={onClick}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        decoding="async"
      />
    </ImageContainer>
  );
}

export type { ArtworkImageProps };
